import { Routes, Route, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProfilePictureProvider } from './contexts/ProfilePictureContext';
import { AppConfigProvider } from './contexts/AppConfigContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { CalendarProvider } from './contexts/CalendarContext';
import { RequestsProvider } from './contexts/RequestsContext';
import { AdminProvider } from './contexts/AdminContext';
import { TenantDataProvider } from './contexts/TenantDataContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { PlanProvider } from './contexts/PlanContext';
import { UpgradePromptProvider } from './contexts/UpgradePromptContext';
import { RoutyProvider } from './contexts/RoutyContext';
import { ErrorBoundary, IntercomProvider, IntercomCompanyUpdater, CommandPalette } from './components/shared';
import AppLoadingScreen from './components/shared/AppLoadingScreen';
import AppInitializer from './components/app/AppInitializer';
import TenantOnlyRoute from './components/shared/TenantOnlyRoute';
import EmbedTokenRoute from './components/shared/EmbedTokenRoute';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import AdminIndexRoute from './components/admin/AdminIndexRoute';
import RoleProtectedRoute from './components/shared/RoleProtectedRoute';
import AuthenticatedLayout from './components/layout/AuthenticatedLayout';
import ImpersonationBanner from './components/shared/ImpersonationBanner';

// ============================================
// Lazy-loaded Pages - Code Splitting
// ============================================

// Auth pages (small, load quickly)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CallbackPage = lazy(() => import('./pages/CallbackPage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const SwitchAccountPage = lazy(() => import('./pages/SwitchAccountPage'));
const CalendarCallbackPage = lazy(() => import('./pages/CalendarCallbackPage'));

// Public booking pages
const EventBookingPage = lazy(() => import('./pages/EventBookingPage'));
const BookingFlowTestPage = lazy(() => import('./pages/BookingFlowTestPage'));
const ChatbotEmbedPage = lazy(() => import('./pages/ChatbotEmbedPage'));
const LandingPagePage = lazy(() => import('./pages/LandingPagePage'));
const RescheduleAppointmentPage = lazy(() => import('./pages/RescheduleAppointmentPage'));
const PartnerAccessApprovalPage = lazy(() => import('./pages/PartnerAccessApprovalPage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const TenantDirectoryDetailPage = lazy(() => import('./pages/TenantDirectoryDetailPage'));

// Main app pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const RequestsPage = lazy(() => import('./pages/RequestsPage'));
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const BookingFlowPage = lazy(() => import('./pages/BookingFlowPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const BookingFormsPage = lazy(() => import('./pages/BookingFormsPage'));
const LandingPagesPage = lazy(() => import('./pages/LandingPagesPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const ContactDetailPage = lazy(() => import('./pages/ContactDetailPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/settings/IntegrationsSettingsPage'));
const VoiceAgentSettingsPage = lazy(() => import('./pages/settings/VoiceAgentSettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const IAMPage = lazy(() => import('./pages/IAMPage'));
const BillingDashboard = lazy(() => import('./pages/BillingDashboard'));
const SchedulingDebugDashboard = lazy(() => import('./pages/schedulingdebug'));
const ScheduleViewPage = lazy(() => import('./pages/ScheduleViewPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const VoiceAgentPage = lazy(() => import('./pages/VoiceAgentPage'));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage'));
const EmbedBookingPage = lazy(() => import('./pages/EmbedBookingPage'));
// Admin pages
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/dashboard/AdminDashboard'));
const TenantListPage = lazy(() => import('./pages/admin/tenants/TenantListPage'));
const PartnerPortalPage = lazy(() => import('./pages/admin/partner-portal/PartnerPortalPage'));
const AccessHubPage = lazy(() => import('./pages/admin/partner-portal/AccessHubPage'));
const PartnerHubPage = lazy(() => import('./pages/admin/partner-hub/PartnerHubPage'));
const AccountLinksPage = lazy(() => import('./pages/admin/account-links/AccountLinksPage'));
const PartnerBillingPage = lazy(() => import('./pages/admin/partner-portal/PartnerBillingPage'));
const PartnerAnalyticsPage = lazy(() => import('./pages/admin/partner-portal/PartnerAnalyticsPage'));
const PartnerResourcesPage = lazy(() => import('./pages/admin/partner-portal/PartnerResourcesPage'));
const PartnerTeamPage = lazy(() => import('./pages/admin/partner-portal/PartnerTeamPage'));
const TenantDetailPage = lazy(() => import('./pages/admin/tenants/TenantDetailPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/analytics/AdminAnalyticsPage'));
const PublicSubmissionBlocksPage = lazy(() => import('./pages/admin/public-submission-blocks/PublicSubmissionBlocksPage'));
const UserListPage = lazy(() => import('./pages/admin/users/UserListPage'));
const RoleListPage = lazy(() => import('./pages/admin/roles/RoleListPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/settings/SettingsPage'));
const InstantBookingPreferencesPage = lazy(() => import('./pages/admin/settings/InstantBookingPreferencesPage'));
const ApplicationListPage = lazy(() => import('./pages/admin/applications/ApplicationListPage'));
const MasterDataListPage = lazy(() => import('./pages/admin/master-data/MasterDataListPage'));
const WebhookListPage = lazy(() => import('./pages/admin/webhooks/WebhookListPage'));
const MessageTemplateListPage = lazy(() => import('./pages/admin/templates/MessageTemplateListPage'));
const DebugSessionsListPage = lazy(() => import('./pages/admin/scheduling-debug/DebugSessionsListPage'));
const DebugSessionDetailPage = lazy(() => import('./pages/admin/scheduling-debug/DebugSessionDetailPage'));
const ApiKeyListPage = lazy(() => import('./pages/admin/api-keys/ApiKeyListPage'));
const EmailActivityListPage = lazy(() => import('./pages/admin/email-activity/EmailActivityListPage'));
const TutorialVideosAdminPage = lazy(() => import('./pages/admin/tutorials/TutorialVideosAdminPage'));
const ChargebeeWebhooksListPage = lazy(() => import('./pages/admin/chargebee-webhooks/ChargebeeWebhooksListPage'));
const ChargebeeWebhookDetailPage = lazy(() => import('./pages/admin/chargebee-webhooks/ChargebeeWebhookDetailPage'));
const RoutyUsagePage = lazy(() => import('./pages/admin/routy-usage/RoutyUsagePage'));
const AdminVoiceAgentPage = lazy(() => import('./pages/admin/voice-agent/AdminVoiceAgentPage'));
const LeadsPage = lazy(() => import('./pages/admin/leads/LeadsPage'));
const AnnouncementsPage = lazy(() => import('./pages/admin/announcements/AnnouncementsPage'));
const TenantOutreachHistoryPage = lazy(() => import('./pages/admin/tenant-outreach/TenantOutreachHistoryPage'));

// Tutorial Videos
const TutorialVideosPage = lazy(() => import('./pages/TutorialVideosPage'));

// Onboarding
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));

// 2FA Setup Wall
const TwoFactorSetupWall = lazy(() => import('./pages/TwoFactorSetupWall'));

// Other
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const RootRedirect = lazy(() => import('./components/shared/RootRedirect'));

// ============================================
// Layout Wrapper Components
// ============================================

/**
 * Wrapper for authenticated routes with layout.
 * Guard checks (onboarding, 2FA) are handled by AppInitializer above the route tree.
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <AuthenticatedLayout>{children}</AuthenticatedLayout>
);

/**
 * Layout route that wraps tenant (non-admin) routes with tenant-specific providers.
 * Admin routes sit outside this so CompanyData, Notifications, Calendar, etc.
 * never mount or make API calls when on /admin/* pages.
 *
 * RequestsProvider lives here (rather than per-route) so its context identity is
 * stable across tenant navigations. Mounting it per-route caused the
 * "useRequests must be used within a RequestsProvider" error during tab switches
 * (the provider unmounted/remounted while consumers still held the old context).
 */
const TenantProviders = () => (
  <TenantDataProvider>
    <IntercomCompanyUpdater />
    <NotificationProvider>
      <CalendarProvider>
        <SidebarProvider>
          <OnboardingProvider>
            <PlanProvider>
              <UpgradePromptProvider>
                <RequestsProvider>
                  <RoutyProvider>
                    <AppInitializer>
                      <Outlet />
                    </AppInitializer>
                  </RoutyProvider>
                </RequestsProvider>
              </UpgradePromptProvider>
            </PlanProvider>
          </OnboardingProvider>
        </SidebarProvider>
      </CalendarProvider>
    </NotificationProvider>
  </TenantDataProvider>
);

function App() {
  const path = window.location.pathname;
  const isPublicEmbed = path.startsWith('/chatbot/') || path.startsWith('/e/') || path.startsWith('/reschedule/') || path.startsWith('/l/') || path === '/directory' || path.startsWith('/directory/') || path.startsWith('/approve-access/');

  if (isPublicEmbed) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          }>
            <Routes>
              <Route path="/chatbot/:formId" element={<ChatbotEmbedPage />} />
              <Route path="/e/:eventId" element={<EventBookingPage />} />
              <Route path="/reschedule/:appointmentId" element={<RescheduleAppointmentPage />} />
              <Route path="/approve-access/:token" element={<PartnerAccessApprovalPage />} />
              <Route path="/l/:landingPageId" element={<LandingPagePage />} />
              <Route path="/directory" element={<DirectoryPage />} />
              <Route path="/directory/:tenantSlug" element={<TenantDirectoryDetailPage />} />
            </Routes>
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
        <AppConfigProvider>
          <IntercomProvider>
            <ProfilePictureProvider>
              <ToastProvider>
                <AdminProvider>
                  <Suspense fallback={<AppLoadingScreen />}>
                    <CommandPalette />
                    <Routes>
                      {/* ============================================
                          Public Booking Routes - No authentication
                          ============================================ */}
                      <Route path="/e/:eventId" element={<EventBookingPage />} />
                      <Route path="/reschedule/:appointmentId" element={<RescheduleAppointmentPage />} />
                      <Route path="/approve-access/:token" element={<PartnerAccessApprovalPage />} />
                      <Route path="/l/:landingPageId" element={<LandingPagePage />} />
                      <Route path="/:businessName/:eventSlug" element={<EventBookingPage />} />
                      <Route path="/booking-test" element={<BookingFlowTestPage />} />

                      {/* ============================================
                          Auth Routes - No authentication required
                          ============================================ */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/callback" element={<CallbackPage />} />
                      <Route path="/calendar-callback" element={<CalendarCallbackPage />} />
                      <Route path="/logout" element={<LogoutPage />} />
                      {/* Account switch landing — arrives with no session on this origin, only
                          the one-time code, so it must stay outside any auth guard. */}
                      <Route path="/switch" element={<SwitchAccountPage />} />

                      {/* ============================================
                          2FA Setup Wall - No tenant providers needed
                          ============================================ */}
                      <Route path="/setup-2fa" element={<TwoFactorSetupWall />} />

                      {/* ============================================
                          Root Route - Initial redirect
                          ============================================ */}
                      <Route path="/" element={<RootRedirect />} />

                      {/* ============================================
                          Admin Routes - NO tenant providers
                          CompanyData, Notifications, Calendar, etc.
                          do not mount here — zero wasted API calls.
                          ============================================ */}
                      <Route path="/admin" element={
                        <AdminProtectedRoute>
                          <AdminLayout />
                        </AdminProtectedRoute>
                      }>
                        <Route index element={<AdminIndexRoute><AdminDashboard /></AdminIndexRoute>} />
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="tenants" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <TenantListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="tenants/:id" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <TenantDetailPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner-hub" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <PartnerHubPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="account-links" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <AccountLinksPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner-portal" element={
                          <AdminProtectedRoute requirePartner>
                            <PartnerPortalPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner/access-hub" element={
                          <AdminProtectedRoute requirePartner>
                            <AccessHubPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner/analytics" element={
                          <AdminProtectedRoute requirePartner>
                            <PartnerAnalyticsPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner/billing" element={
                          <AdminProtectedRoute requirePartner requirePartnerOwner>
                            <PartnerBillingPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner/resources" element={
                          <AdminProtectedRoute requirePartner>
                            <PartnerResourcesPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="partner/team" element={
                          <AdminProtectedRoute requirePartner>
                            <PartnerTeamPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="analytics" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <AdminAnalyticsPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="security/public-submission-blocks" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <PublicSubmissionBlocksPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="users" element={
                          <AdminProtectedRoute>
                            <UserListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="roles" element={
                          <AdminProtectedRoute>
                            <RoleListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="settings" element={
                          <AdminProtectedRoute>
                            <AdminSettingsPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="settings/instant-booking" element={
                          <AdminProtectedRoute>
                            <InstantBookingPreferencesPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="applications" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <ApplicationListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="master-data" element={
                          <AdminProtectedRoute>
                            <MasterDataListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="webhooks" element={
                          <AdminProtectedRoute>
                            <WebhookListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="templates" element={
                          <AdminProtectedRoute>
                            <MessageTemplateListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="scheduling-debug" element={
                          <AdminProtectedRoute>
                            <DebugSessionsListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="scheduling-debug/:sessionId" element={
                          <AdminProtectedRoute>
                            <DebugSessionDetailPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="iam" element={
                          <AdminProtectedRoute>
                            <IAMPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="api-keys" element={
                          <AdminProtectedRoute>
                            <ApiKeyListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="email-activity" element={
                          <AdminProtectedRoute>
                            <EmailActivityListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="tutorials" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <TutorialVideosAdminPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="routy-usage" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <RoutyUsagePage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="voice-agent" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <AdminVoiceAgentPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="chargebee-webhooks" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <ChargebeeWebhooksListPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="chargebee-webhooks/:id" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <ChargebeeWebhookDetailPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="leads" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <LeadsPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="announcements" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <AnnouncementsPage />
                          </AdminProtectedRoute>
                        } />
                        <Route path="tenant-outreach" element={
                          <AdminProtectedRoute requireHostAdmin>
                            <TenantOutreachHistoryPage />
                          </AdminProtectedRoute>
                        } />
                      </Route>

                      {/* ============================================
                          Tenant Routes - Wrapped in TenantProviders
                          CompanyData, Notifications, Calendar, Sidebar,
                          Onboarding, and AppInitializer only mount here.
                          ============================================ */}
                      <Route element={<TenantProviders />}>
                        {/* Standalone chrome-free CreateBookingModal for iframe embedding
                            (e.g. DripJobs). Authenticated via short-lived embed token passed in
                            the URL — bypasses oidc session requirement so the route works in
                            third-party iframes on iOS Safari where localStorage is partitioned.
                            EmbedTokenRoute never redirects to /login (blocked by X-Frame-Options). */}
                        <Route path="/embed/new-booking" element={
                          <EmbedTokenRoute>
                            <EmbedBookingPage />
                          </EmbedTokenRoute>
                        } />
                        <Route path="/onboarding" element={
                          <TenantOnlyRoute>
                            {/* The wizard is a full-screen fixed overlay rendered
                                outside AuthenticatedLayout, so the impersonation
                                banner (normally in that layout) is absent here.
                                Pin it above the overlay so staff can stop
                                impersonating a new client without completing setup.
                                Renders nothing when not impersonating. */}
                            <div className="fixed top-0 inset-x-0 z-[70]">
                              <ImpersonationBanner />
                            </div>
                            <OnboardingWizard />
                          </TenantOnlyRoute>
                        } />
                        <Route path="/dashboard" element={
                          <ProtectedRoute><DashboardPage /></ProtectedRoute>
                        } />
                        <Route path="/requests" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canManageRequests">
                              <RequestsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/bookings" element={
                          <ProtectedRoute><BookingsPage /></ProtectedRoute>
                        } />
                        <Route path="/booking-flow" element={
                          <ProtectedRoute><BookingFlowPage /></ProtectedRoute>
                        } />
                        <Route path="/calendar" element={
                          <ProtectedRoute><CalendarPage /></ProtectedRoute>
                        } />
                        <Route path="/booking-forms" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canEditBookingForms">
                              <BookingFormsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/landing-pages" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canEditBookingForms">
                              <LandingPagesPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/contacts" element={
                          <ProtectedRoute><ContactsPage /></ProtectedRoute>
                        } />
                        <Route path="/contacts/:id" element={
                          <ProtectedRoute><ContactDetailPage /></ProtectedRoute>
                        } />
                        <Route path="/notifications" element={
                          <ProtectedRoute><NotificationsPage /></ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canEditCompanySettings">
                              <SettingsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/settings/integrations" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canEditCompanySettings">
                              <IntegrationsSettingsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/settings/voice-agents" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canEditCompanySettings">
                              <VoiceAgentSettingsPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/profile" element={
                          <ProtectedRoute><ProfilePage /></ProtectedRoute>
                        } />
                        <Route path="/iam" element={
                          <ProtectedRoute><IAMPage /></ProtectedRoute>
                        } />
                        <Route path="/billing" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canViewBilling">
                              <BillingDashboard />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/scheduling-debug" element={
                          <ProtectedRoute><SchedulingDebugDashboard /></ProtectedRoute>
                        } />
                        <Route path="/schedule-view" element={
                          <ProtectedRoute><ScheduleViewPage /></ProtectedRoute>
                        } />
                        <Route path="/analytics" element={
                          <ProtectedRoute><AnalyticsPage /></ProtectedRoute>
                        } />
                        <Route path="/voice-agent" element={
                          <ProtectedRoute>
                            <RoleProtectedRoute requiredPermission="canAccessVoiceAgent">
                              <VoiceAgentPage />
                            </RoleProtectedRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="/tutorials" element={
                          <ProtectedRoute><TutorialVideosPage /></ProtectedRoute>
                        } />
                        <Route path="/design-system" element={
                          <ProtectedRoute><DesignSystemPage /></ProtectedRoute>
                        } />
                      </Route>

                      {/* ============================================
                          404 Not Found - Must be last
                          ============================================ */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </AdminProvider>
              </ToastProvider>
            </ProfilePictureProvider>
          </IntercomProvider>
        </AppConfigProvider>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
